import React from 'react';
import Grid from '@mui/material/Grid';
import { useAdminUserOverviewQuery, useAdminUserQuery } from '../../api/admin';
import { formatDateTime, relativeDay } from '../../utils/dates';
import OverviewPanel from './OverviewPanel';
import StatTile from './StatTile';
import TaskTrailPanel, { LastTouchpointTile } from './TaskTrailPanel';
import { userLabel } from './taskOptions';
import UserObjectsTable from './UserObjectsTable';
import UserPageViewsChart from './UserPageViewsChart';
import UserPageViewsTable from './UserPageViewsTable';

interface Props {
	userId: number;
}

/** Gap between boxes. */
const GAP = 2.5;
/** Height of the two bottom tables (Page Views, User Objects). */
const TABLE_HEIGHT = 440;
export default function UserOverviewTab({ userId }: Props) {
	const { data: o, isLoading } = useAdminUserOverviewQuery(userId);
	// Already loaded by the page header; only needed here to name the user in a new task's "Tag to".
	const { data: user } = useAdminUserQuery(userId);
	const TILE = 2.4;

	return (
		<>
			<Grid container spacing={GAP}>
				<Grid item xs={12} md={8}>
					<Grid container spacing={GAP} sx={{ mb: GAP }}>
						<Grid item xs={6} sm={TILE}>
							<StatTile
								label="Last activity"
								value={relativeDay(o?.last_activity ?? null)}
								caption={o?.last_activity ? formatDateTime(o.last_activity) : 'no activity recorded'}
								secondary={
									o ? `web ${relativeDay(o.last_page_view)} · MCP ${relativeDay(o.last_mcp_call)}` : undefined
								}
								loading={isLoading}
							/>
						</Grid>
						<Grid item xs={6} sm={TILE}>
							<LastTouchpointTile subject={{ userId }} />
						</Grid>
						<Grid item xs={6} sm={TILE}>
							<StatTile
								label="Total User Objects"
								value={o ? o.views_count + o.tables_count + o.screeners_count : null}
								caption="views + worksheets (tables) + screeners"
								secondary={
									o
										? `${o.views_count} views · ${o.tables_count} worksheets · ${o.screeners_count} screeners`
										: undefined
								}
								loading={isLoading}
							/>
						</Grid>
						<Grid item xs={6} sm={TILE}>
							<StatTile
								label="Page views"
								value={o?.page_views_7d ?? null}
								caption="last 7 days"
								secondary={o ? `${o.page_views_30d.toLocaleString()} in the last 30 days` : undefined}
								loading={isLoading}
							/>
						</Grid>
						<Grid item xs={6} sm={TILE}>
							<StatTile
								label="MCP calls"
								value={o?.mcp_calls_7d ?? null}
								caption="last 7 days"
								secondary={o ? `${o.mcp_calls_30d.toLocaleString()} in the last 30 days` : undefined}
								loading={isLoading}
							/>
						</Grid>
					</Grid>
					<OverviewPanel title="Page views per day" caption="last 30 days" sx={{ height: 'auto' }}>
						<UserPageViewsChart userId={userId} />
					</OverviewPanel>
				</Grid>
				<Grid item xs={12} md={4}>
					<TaskTrailPanel subject={{ userId }} subjectName={user && userLabel(user)} fill />
				</Grid>
			</Grid>

			<Grid container spacing={GAP} sx={{ mt: 0 }}>
				<Grid item xs={12} md={6}>
					<OverviewPanel title="Page Views" caption="newest first">
						<UserPageViewsTable userId={userId} height={TABLE_HEIGHT} />
					</OverviewPanel>
				</Grid>
				<Grid item xs={12} md={6}>
					<OverviewPanel title="User Objects" caption="views, worksheets, screeners · newest first · click to open its tab">
						<UserObjectsTable userId={userId} height={TABLE_HEIGHT} />
					</OverviewPanel>
				</Grid>
			</Grid>
		</>
	);
}
